

<template>
  <div>
    <!-- Card di contenimento della gestione premi -->
    <b-card
      title="Dipendenti e premi"
      class="mr-1 ml-1 mt-3"
    >
      <!-- Filtri e controlli della tabella -->
      <b-row class="mb-2">
        <!-- Elementi visualizzati per pagina -->
        <b-col cols="12" md="2">
          <b-form-group label="Elementi visualizzati:" label-for="page-size-premi">
            <b-form-select
              id="page-size-premi"
              v-model="page_size_premi"
              :options="page_size_premi_opt"
            />
          </b-form-group>
        </b-col>

        <!-- Filtro per stato della scelta -->
        <b-col cols="12" md="2">
          <b-form-group label="Stato:" label-for="filtro-stato-premi">
            <b-form-select
              id="filtro-stato-premi"
              v-model="filtro_stato_premi"
              :options="filtro_stato_premi_opt"
            />
          </b-form-group>
        </b-col>

        <!-- Ricerca nella tabella -->
        <b-col cols="12" md="3">
          <b-form-group label="Ricerca per termine:" label-for="filtro-premi">
            <b-form-input
              id="filtro-premi"
              v-model="filter_premi"
              placeholder="Cerca dipendente..."
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <!-- Tabella dipendenti e premi -->
          <b-table
            ref="tabellaPremi"
            responsive
            stacked="sm"
            :items="dipendenti_premi_filtrati"
            :fields="fields_premi"
            :per-page="page_size_premi"
            :current-page="page_premi"
            :filter="filter_premi"
            show-empty
            @filtered="onPremiFiltered"
          >
            <!-- Nominativo del dipendente -->
            <template v-slot:cell(dipendente)="data">
              {{ data.item.dipendente }}
            </template>

            <!-- Premio lordo modificabile per campagne non chiuse -->
            <template v-slot:cell(valore)="data">
              <div style="width: 120px;">
                <!-- Visualizzazione del premio per campagne chiuse -->
                <div
                  v-if="campagna.stato === 'CHIUSO'"
                  class="text-right pr-2"
                >
                  {{ formatImporto(data.item.valore) }}
                </div>

                <!-- Campo modificabile per campagne non chiuse -->
                <b-input-group v-else size="sm">
                  <b-form-input
                    :value="formatImportoInput(data.item.valore)"
                    type="text"
                    inputmode="decimal"
                    class="text-right border-right-0 pr-0"
                    @focus="$event.target.select()"
                    @blur="aggiornaPremio(data.item, $event.target.value, $event.target)"
                  />
                  <b-input-group-append>
                    <span class="input-group-text bg-white border-left-0">€</span>
                  </b-input-group-append>
                </b-input-group>
              </div>
            </template>

            <!-- Maggiorazione personalizzata modificabile per campagne non chiuse -->
            <template v-slot:cell(percentuale_maggiorazione)="data">
              <div class="d-flex align-items-center">
                <!-- Visualizzazione della maggiorazione per campagne chiuse -->
                <span
                  v-if="campagna.stato === 'CHIUSO'"
                  class="d-inline-block text-right pr-2"
                  style="width: 110px;"
                >
                  {{ data.item.percentuale_maggiorazione === null
                    ? String(campagna.percentuale_maggiorazione).replace('.', ',')
                    : String(data.item.percentuale_maggiorazione).replace('.', ',')
                  }}%
                </span>

                <!-- Campo modificabile per campagne non chiuse -->
                <b-input-group
                  v-else
                  size="sm"
                  style="width: 110px; flex-shrink: 0;"
                >
                  <b-form-input
                    :value="data.item.percentuale_maggiorazione"
                    :disabled="!data.item.valore || Number(data.item.valore) <= 0"
                    type="text"
                    inputmode="decimal"
                    :placeholder="String(campagna.percentuale_maggiorazione).replace('.', ',')"
                    class="text-right border-right-0 bg-white"
                    style="padding-right: 4px;"
                    @change="aggiornaMaggiorazione(data.item, $event)"
                    @blur="$event.target.value = data.item.percentuale_maggiorazione === null ? '' : String(data.item.percentuale_maggiorazione).replace('.', ',')"
                  />
                  <b-input-group-append>
                    <span
                      class="input-group-text bg-white border-left-0"
                      style="padding: 0 2px 0 0;"
                    >%</span>
                  </b-input-group-append>
                </b-input-group>

                <!-- Indicatore della provenienza della maggiorazione -->
                <!-- Accessibilità:
                     - hover.focus: mostra il tooltip con mouse o focus da tastiera.
                     - tabindex="0": permette di raggiungere il badge tramite Tab.
                     - aria-label: fornisce la descrizione completa agli screen reader (es. VoiceOver),
                       evitando che venga letta soltanto la lettera P o C. -->
                <b-badge
                  v-b-tooltip.hover.focus
                  tabindex="0"
                  :title="data.item.percentuale_maggiorazione === null
                    ? 'Maggiorazione della campagna'
                    : 'Maggiorazione personalizzata'"
                  :aria-label="data.item.percentuale_maggiorazione === null
                    ? 'Maggiorazione della campagna'
                    : 'Maggiorazione personalizzata'"
                  :variant="data.item.percentuale_maggiorazione === null ? 'secondary' : 'info'"
                  class="ml-2"
                >
                  {{ data.item.percentuale_maggiorazione === null ? 'C' : 'P' }}
                </b-badge>
              </div>
            </template>

            <!-- Valore welfare potenziale calcolato in base al premio e alla maggiorazione -->
            <template v-slot:cell(welfare)="data">
              {{ formatImporto(calcolaWelfare(data.item)) }}
            </template>

            <!-- Scelta del dipendente -->
            <template v-slot:cell(scelta)="data">
              {{ data.item.scelta || '—' }}
            </template>

            <!-- Data della scelta -->
            <template v-slot:cell(data_scelta)="data">
              {{ data.item.data_scelta ? formatDataScelta(data.item.data_scelta) : '—' }}
            </template>

            <!-- Nessun dipendente disponibile -->
            <template v-slot:empty>
              <p class="text-center mb-0">Nessun dipendente disponibile</p>
            </template>

            <!-- Nessun risultato della ricerca -->
            <template v-slot:emptyfiltered>
              <p class="text-center mb-0">Nessun dipendente trovato</p>
            </template>
          </b-table>

          <!-- Legenda della provenienza della maggiorazione -->
          <div class="d-flex flex-wrap align-items-center mb-3">
            <div class="d-flex align-items-center mr-4">
              <b-badge variant="info" class="mr-2">P</b-badge>
              <span class="small">Maggiorazione personalizzata</span>
            </div>
            <div class="d-flex align-items-center">
              <b-badge variant="secondary" class="mr-2">C</b-badge>
              <span class="small">Maggiorazione della campagna</span>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- Paginatore della tabella e salvataggio dei premi -->
      <b-row class="align-items-center mt-3">
        <!-- Spazio per mantenere il paginatore centrato -->
        <b-col cols="12" md="3"></b-col>

        <!-- Paginatore della tabella -->
        <b-col cols="12" md="6" class="d-flex justify-content-center">
          <b-pagination
            v-model="page_premi"
            :per-page="page_size_premi"
            :total-rows="items_tot_premi_ricerca !== null ? items_tot_premi_ricerca : items_tot_premi"
            align="center"
            class="mb-0"
          />
        </b-col>

        <!-- Salvataggio dei premi e delle maggiorazioni per campagne non chiuse -->
        <b-col
          v-if="campagna.stato !== 'CHIUSO'"
          cols="12"
          md="3"
          class="text-right"
        >
          <b-button
            variant="primary"
            :disabled="numero_modifiche === 0 || salvataggio_in_corso"
            @click="$emit('salva-premi')"
          >
            <b-spinner
              v-if="salvataggio_in_corso"
              small
              class="mr-1"
            />
            <b-icon
              v-else
              icon="check-circle"
              class="mr-1"
            />
            {{ salvataggio_in_corso
              ? "Salvataggio..."
              : campagna.stato === "PUBBLICATO"
                ? "Salva e invia notifiche"
                : "Salva"
            }}
          </b-button>
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>


<script>
import moment from "moment";

export default {
  name: "CPDipendentiPremi",

  props: {
    // Campagna selezionata dal componente principale.
    campagna: { type: Object, required: true },

    // Elenco dei dipendenti e dei premi della campagna.
    dipendenti_premi: { type: Array, required: true },

    // Numero di dipendenti con premio o maggiorazione modificati.
    numero_modifiche: { type: Number, default: 0 },

    // Indica se il salvataggio dei premi è in corso.
    salvataggio_in_corso: { type: Boolean, default: false }

  },

  data() {
    return {
      // Gestione della paginazione e dei filtri della tabella premi.
      page_premi: 1,
      page_size_premi: 10,
      page_size_premi_opt: [10, 25, 50, 100, 500],
      filter_premi: "",
      filtro_stato_premi: null,
      filtro_stato_premi_opt: [
        { value: null, text: "Tutti" },
        { value: "Nessun premio", text: "Nessun premio" },
        { value: "In attesa", text: "In attesa" },
        { value: "Confermato", text: "Confermato" }
      ],

      // Colonne della tabella dipendenti e premi.
      fields_premi: [
        { key: "dipendente", label: "Dipendente", sortable: true },
        { key: "valore", label: "Premio lordo", sortable: true },
        { key: "percentuale_maggiorazione", label: "Maggiorazione", sortable: true },
        { key: "welfare", label: "Welfare", sortable: true },
        { key: "scelta", label: "Scelta", sortable: true },
        { key: "data_scelta", label: "Data scelta", sortable: true },
        { key: "stato", label: "Stato", sortable: true }
      ],

      // Numero di dipendenti risultanti dalla ricerca testuale.
      items_tot_premi_ricerca: null
    };
  },

  beforeDestroy() {
    // Interrompe l'osservazione quando il componente viene distrutto.
    if (this.observer_tabella_premi) {
      this.observer_tabella_premi.disconnect();
    }
  },

  computed: {
    // Filtra i dipendenti in base allo stato della scelta.
    dipendenti_premi_filtrati() {
      if (!this.filtro_stato_premi) {
        return this.dipendenti_premi;
      }

      return this.dipendenti_premi.filter(
        dipendente => dipendente.stato === this.filtro_stato_premi
      );
    },

    // Restituisce il numero di dipendenti dopo l'applicazione dei filtri.
    items_tot_premi() {
      return this.dipendenti_premi_filtrati.length;
    }
  },

  watch: {
    // Ripristina filtri e paginazione quando cambia la campagna.
    "campagna.id"() {
      this.page_premi = 1;
      this.filtro_stato_premi = null;
      this.filter_premi = "";
      this.items_tot_premi_ricerca = null;
    }
  },

  methods: {
    // Formatta gli importi in euro.
    formatImporto(valore) {
      return new Intl.NumberFormat("it-IT", {
        style: "currency",
        currency: "EUR",
        useGrouping: true
      }).format(Number(valore || 0));
    },

    // Formatta la data della scelta.
    formatDataScelta(data) {
      return moment(data).format("DD/MM/YYYY");
    },

    // Aggiorna il numero di risultati e ripristina la prima pagina.
    onPremiFiltered(items) {
      this.items_tot_premi_ricerca = items.length;
      this.page_premi = 1;
    },

    // Formatta il premio con due decimali, senza simbolo euro.
    formatImportoInput(valore) {
      if (valore === null || valore === undefined || valore === "") {
        return "0,00";
      }

      return Number(valore).toLocaleString("it-IT", {
        useGrouping: true,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    // Aggiorna il valore numerico del premio.
    aggiornaPremio(dipendente, valore, input) {
      const importo = String(valore).trim().replace(/\s/g, "");
      const formatoValido = /^(?:\d+|\d{1,3}(?:\.\d{3})+)(?:,\d{1,2})?$/.test(importo);

      if (!formatoValido) {
        this.$bvToast.toast("Inserire un importo valido maggiore o uguale a 0, con massimo due decimali.", {
          title: "Premio non valido",
          variant: "danger",
          solid: true
        });

        // Ripristina l'ultimo importo valido.
        input.value = this.formatImportoInput(dipendente.valore);
        return;
      }

      const numero = Number(importo.replace(/\./g, "").replace(",", "."));

      // Aggiorna il valore visualizzato nel formato italiano.
      input.value = this.formatImportoInput(numero);

      // Comunica al componente principale il nuovo importo.
      this.$emit("aggiorna-premio", {
        dipendente,
        valore: numero
      });
    },

    // Aggiorna e valida la maggiorazione personalizzata del dipendente.
    aggiornaMaggiorazione(dipendente, valore) {
      const percentuale = String(valore).trim().replace(/\s/g, "");

      // Campo vuoto: utilizza la maggiorazione generale della campagna.
      if (percentuale === "") {
        this.$emit("aggiorna-maggiorazione", {
          dipendente,
          valore: null
        });
        return;
      }

      // Accetta solo valori positivi con massimo due decimali.
      const formatoValido = /^\d+(?:[,.]\d{1,2})?$/.test(percentuale);
      const numero = Number(percentuale.replace(",", "."));

      if (!formatoValido || !Number.isFinite(numero) || numero <= 0) {
        this.$bvToast.toast(
          "Inserire una maggiorazione superiore a 0, con massimo due decimali",
          {
            title: "Maggiorazione non valida",
            variant: "danger",
            solid: true
          }
        );
        return;
      }

      // Comunica al componente principale la nuova maggiorazione.
      this.$emit("aggiorna-maggiorazione", {
        dipendente,
        valore: numero
      });
    },

    // Calcola il valore welfare potenziale applicando la maggiorazione personalizzata o quella della campagna.
    calcolaWelfare(dipendente) {
      const premio = Number(dipendente.valore) || 0;

      const maggiorazione = (
        dipendente.percentuale_maggiorazione === null ||
        dipendente.percentuale_maggiorazione === ""
      )
        ? Number(this.campagna.percentuale_maggiorazione)
        : Number(dipendente.percentuale_maggiorazione);

      return Math.round((premio * (1 + maggiorazione / 100) + Number.EPSILON) * 100) / 100;
    }
  }
};
</script>

